import { useState, useEffect } from 'react';
import { Alert, Button, Box, Typography } from '@mui/material';
import SetupIcon from '@mui/icons-material/RocketLaunch';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../providers/api-rest-provider';

const DISMISSED_KEY = 'orchestra_onboarding_dismissed';

interface TenantSelf {
  orgName: string | null;
  checklist: { key: string; complete: boolean }[];
  seats: { used: number; total: number } | null;
}

async function fetchTenantSelf(): Promise<TenantSelf> {
  const raw = await apiFetch('tenants/self');
  return {
    orgName: raw?.name ?? null,
    checklist: Object.entries(raw?.checklist ?? {}).map(([key, complete]) => ({
      key,
      complete: Boolean(complete),
    })),
    seats: raw?.seats ? { used: raw.seats.used, total: raw.seats.total } : null,
  };
}

export const OnboardingBanner = () => {
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<TenantSelf | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(
    () => localStorage.getItem(DISMISSED_KEY) === '1',
  );

  useEffect(() => {
    if (dismissed) return;
    fetchTenantSelf().then(setTenant).catch(() => {
      // Silently fail — don't block the app
    });
  }, [dismissed]);

  if (dismissed) return null;
  if (!tenant) return null;

  const incomplete = tenant.checklist.filter((c) => !c.complete);

  if (incomplete.length === 0) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
  };

  const handleContinue = () => {
    navigate('/onboarding');
  };

  return (
    <Box sx={{ px: 2, pt: 1 }}>
      <Alert
        severity="info"
        icon={<SetupIcon />}
        onClose={handleDismiss}
        action={
          <Box display="flex" gap={1} alignItems="center">
            {tenant.seats && (
              <Typography variant="caption" sx={{ whiteSpace: 'nowrap', mr: 1 }}>
                Seats: {tenant.seats.used} / {tenant.seats.total} used
              </Typography>
            )}
            <Button
              size="small"
              variant="contained"
              color="info"
              onClick={handleContinue}
            >
              Continue Setup
            </Button>
          </Box>
        }
        sx={{ alignItems: 'center' }}
      >
        Complete your setup ({incomplete.length} step{incomplete.length !== 1 ? 's' : ''} remaining)
      </Alert>
    </Box>
  );
};
